import { Box, CircularProgress, Typography } from '@mui/material'
import { toast } from 'react-hot-toast'
import {
  describeChanges,
  LineRecalculation,
  recalculationError,
  RowProblem,
} from './lineRecalculation'

const SHOWN = 3

const ProblemList = ({
  problems,
  tone,
  title,
}: {
  problems: RowProblem[]
  tone: 'problem' | 'notice'
  title: string
}) => {
  if (!problems.length) return null
  const color =
    tone === 'problem' ? 'var(--state-failed-fg)' : 'var(--amber-fg)'
  return (
    <Box sx={{ color }}>
      <Typography variant="caption" sx={{ fontWeight: 600, color }}>
        {title}
      </Typography>
      <Box component="ul" sx={{ m: 0, pl: 4 }}>
        {problems.slice(0, SHOWN).map((p, i) => (
          <Typography
            key={`${p.message}-${i}`}
            component="li"
            variant="caption"
            sx={{ display: 'list-item', color }}
          >
            {p.message}
          </Typography>
        ))}
        {problems.length > SHOWN && (
          <Typography
            component="li"
            variant="caption"
            sx={{ display: 'list-item', color }}
          >
            and {problems.length - SHOWN} more (marked in the grid)
          </Typography>
        )}
      </Box>
    </Box>
  )
}

/**
 * The preview's note above a line-settings grid: what the edits change, then
 * any edits the rules refuse (they block Save and are marked red in the grid)
 * and any conflicts that were there already (amber, not blocking).
 */
export const LinePreviewNote = ({
  summary,
  isPreviewing,
  problems = [],
}: {
  summary: string | null
  isPreviewing: boolean
  problems?: RowProblem[]
}) => {
  if (!summary && !isPreviewing && !problems.length) return null
  const blocking = problems.filter((p) => p.blocking)
  const notices = problems.filter((p) => !p.blocking)
  return (
    <Box
      role="status"
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: 1,
        px: 2,
        py: 1,
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
        {isPreviewing && <CircularProgress size={12} />}
        <Typography variant="caption" color="text.secondary">
          {summary ?? 'Recalculating…'}
        </Typography>
      </Box>
      <ProblemList
        problems={blocking}
        tone="problem"
        title={`Can't save until ${
          blocking.length === 1 ? 'this is' : `these ${blocking.length} are`
        } fixed:`}
      />
      <ProblemList
        problems={notices}
        tone="notice"
        title="Already in the data (not blocking):"
      />
    </Box>
  )
}

export const toastLineSaved = (result: LineRecalculation) =>
  toast.success(`Saved: ${describeChanges(result)}`)

export const toastLineSaveFailed = (error: unknown) =>
  toast.error(`Not saved: ${recalculationError(error)}`)
